import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types/portal';
import { isLoginRedirectInProgress } from '../../services/authRedirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, isLoading } = useAppSelector((state) => state.auth);
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login (preserve returnTo)
  if (!isAuthenticated || !user) {
    if (isLoginRedirectInProgress()) {
      // Avoid double-redirect flicker if API layer already initiated it
      return null;
    }
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to={`/portal/login?returnTo=${returnTo}`} replace />;
  }

  // If server requires password change, always redirect to the set-password page unless already there
  if (user.requirePasswordChange && location.pathname !== '/portal/set-password') {
    return <Navigate to="/portal/set-password" replace />;
  }

  // Check if user role is allowed - redirect to their appropriate dashboard
  if (!allowedRoles.includes(user.role)) {
    // Determine the correct dashboard path based on user role
    const redirectPath = user.role === UserRole.STUDENT
      ? '/portal/student'
      : user.role === UserRole.TEACHER
      ? '/portal/teacher'
      : user.role === UserRole.ADMIN
      ? '/portal/admin'
      : '/';

    return <Navigate to={redirectPath} replace />;
  }

  // User is authenticated and authorized, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;